/* eslint-disable camelcase */
import {
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  createStyles,
  Drawer,
  IconButton,
  makeStyles,
  // SwipeableDrawer,
  Theme
} from '@material-ui/core';
import {_closePatientDrawer} from '@shared/store/actions/PatientDrawerAction';
import React, {useEffect, useRef} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {
  BORDER_BOTTOM_COLOR,
  BORDER_COLOR,
  DEFAULT_WHITE_COLOR,
  FONT_PRIMARY_COLOR,
  FONT_SECONDARY_COLOR
} from 'src/style/variable';

import './PatientDetailsHome.scss';
import PatientDetailsTopBar from './Components/PatientDetailsTopBar/PatientDetailsTopBar';
import PatientDetailsContainer from './Components/PatientDetailsContainer/PatientDetailsContainer';
import PatientDetailsSideBar from './Components/PatientDetailsSidebar/PatientDetailsSidebar';
import Loader from '@components/framework/loader/Loader';
import {useTranslation} from 'react-i18next';
import {SnackbarKey, useSnackbar} from 'notistack';
import {Cancel} from '@material-ui/icons';
import {CommunicationService} from 'src/service/CommunicationService';
import {State} from '@shared/store/reducers/Index';
import {Permission} from '@shared/services/PermissionService';
import {AppPermissions} from '@shared/constants/Mapping';
const styles = makeStyles((theme: Theme) =>
  createStyles({
    drawer: {
      '& .MuiBackdrop-root': {
        backgroundColor: 'transparent'
      },
      '& .MuiDrawer-paperAnchorRight': {
        '@media (max-width:1024px)': {
          width: '100%',
          height: 'calc(100% - 40px)'
        },
        '@media (max-width:1336px)': {
          width: '100%',
          height: 'calc(100% - 40px)'
        },
        '@media (max-width:1920px)': {
          width: '100%',
          height: 'calc(100% - 40px)'
        },
        backgroundColor: DEFAULT_WHITE_COLOR,
        marginTop: theme.mixins.toolbar.minHeight,
        zIndex: 1300,
        borderLeft: `1px solid ${BORDER_COLOR}`,
        overflow: 'hidden'
      },
      '& .MuiPaper-elevation16': {
        boxShadow: 'none'
      }
    },
    leftDiv: {
      flexBasis: '10%',
      [theme.breakpoints.down('md')]: {
        display: 'none'
      },
      height: '100%',
      position: 'sticky',
      background: '#F9F9F9'
    },
    rightDiv: {
      paddingInline: '1.5rem',
      overflowY: 'auto',
      flex: 1,
      overflowX: 'hidden'
    },
    loaderStyle: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      height: '100%'
    },
    errorContainer: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      height: '100%'
    },
    errorWrapper: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: '1rem'
    }
  })
);

interface StartTimerReminderToastProps {
  id: SnackbarKey | undefined;
  style?: React.CSSProperties;
  ccmPatientData: any;
  onStartTime: () => void;
}

const useStartTimerReminderToastClasses = makeStyles(() =>
  createStyles({
    cardHeader: {
      display: 'flex',
      alignItems: 'center',
      gap: '2rem',
      // backgroundColor: APP_PRIMARY_COLOR,
      color: FONT_PRIMARY_COLOR,
      fontSize: '1.5rem',
      borderBottom: `1px solid ${BORDER_BOTTOM_COLOR}`,
      '& .MuiCardHeader-action': {
        alignSelf: 'center',
        margin: 0
      }
    },
    cancelButton: {
      padding: 0,
      color: FONT_SECONDARY_COLOR
    },
    cardActions: {
      padding: '1rem'
    }
  })
);

const StartTimerReminderToast = React.forwardRef<
  HTMLDivElement,
  StartTimerReminderToastProps
>((props, ref) => {
  const {closeSnackbar} = useSnackbar();
  const {id, ccmPatientData, onStartTime} = props;
  const classes = useStartTimerReminderToastClasses();
  const {t} = useTranslation();
  return (
    <Card ref={ref} elevation={4}>
      <CardHeader
        disableTypography
        className={classes.cardHeader}
        title={t('CCM.CCM_REMINDER_TITLE')}
        action={
          <IconButton
            onClick={() => {
              closeSnackbar(id);
            }}
            className={classes.cancelButton}>
            <Cancel fontSize="large" />
          </IconButton>
        }
      />
      <CardContent>
        {t('CCM.CCM_REMINDER_DIALOG_BODY', {
          patientName: ccmPatientData?.patientName || ''
        })}
      </CardContent>
      <CardActions className={classes.cardActions}>
        <Button variant="contained" color="primary" onClick={onStartTime}>
          {t('CCM.START_TIMER.START_TIMER')}
        </Button>
      </CardActions>
    </Card>
  );
});

interface PatientDetailsHomeProps {
  openHomeDrawer: boolean;
}

const PatientsDetailsHome = (
  props: PatientDetailsHomeProps
): React.ReactElement => {
  const classes = styles();
  const {openHomeDrawer} = props;
  const dispatch = useDispatch();
  const {t} = useTranslation();

  const firstRender = useRef(true);
  const toasterId = useRef<SnackbarKey>();

  const closeDrawer = (): void => {
    dispatch(_closePatientDrawer());
  };

  const {isLoading, isError, patientDetails, groupId, clinicId} = useSelector(
    (state: any) => state.patientDetailsDrawer
  );

  const timerInfo: any = useSelector((state: State) => state.ccmTimer);

  const {enqueueSnackbar, closeSnackbar} = useSnackbar();

  const patientFullName = `${patientDetails?.['first_name'] ?? ''} ${
    patientDetails?.['middle_name'] ?? ''
  } ${patientDetails?.['last_name'] ?? ''}`;

  const closeStartTimerReminder = () => {
    if (toasterId.current) {
      closeSnackbar(toasterId.current);
    }
  };

  const handleStartTime = () => {
    CommunicationService.sendEvent({
      action: 'START_TIMER_FROM_PATIENT_DRAWER_REMINDER',
      data: null
    });
    closeStartTimerReminder();
  };

  const isTimeRunningForCurrentPatient =
    timerInfo.isTimerRunning &&
    timerInfo.ccmPatientId == patientDetails?.ccm_patient_id;

  useEffect(() => {
    if (isTimeRunningForCurrentPatient) {
      closeStartTimerReminder();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTimeRunningForCurrentPatient]);

  const clinicsList: any = useSelector(
    (state: any) => state.clinics.list || []
  );
  const {userPermissions} = useSelector((state: State) => state.auth);

  const hasCCMPermission = React.useMemo(() => {
    // USER DOESN'T HAVE CCM PERMISSION
    if (
      !Permission.hasCCMPermissions(
        userPermissions,
        AppPermissions.CCM.LIST,
        groupId
      )
    ) {
      return false;
    }

    // IF PATIENT SHARED IS ON THEN CCM MUST BE ENABLED AT GROUP
    if (
      userPermissions?.[groupId]?.['group']?.[
        AppPermissions.PATIENT_MENU_PERMISSIONS.PATIENT_SHARED
      ] === true &&
      userPermissions?.[groupId]?.['group']?.[
        AppPermissions.CCM_MENU_PERMISSIONS.CCM
      ] === true
    ) {
      return true;
    }

    const hasClinicListBeenUpdated = clinicsList.some(
      (cl: any) => cl.org_group_id === groupId
    );
    if (!hasClinicListBeenUpdated) {
      return false;
    }

    if (
      userPermissions?.[groupId]?.['group']?.[
        AppPermissions.PATIENT_MENU_PERMISSIONS.PATIENT_SHARED
      ] === false
    ) {
      if (clinicId) {
        const selectedClinic = clinicsList.find((c: any) => c.id === clinicId);
        if (selectedClinic && selectedClinic.is_ccm_enabled) {
          return true;
        }
        return false;
      }
      return false;
    }
    return false;
  }, [clinicsList, groupId, userPermissions, clinicId]);

  // check if ccm is enabled for at the groupLevel for patient shared on and for the practice in the patient shared off
  useEffect(() => {
    if (
      !isLoading &&
      firstRender.current &&
      !isTimeRunningForCurrentPatient &&
      patientDetails?.ccm_patient_id &&
      hasCCMPermission &&
      patientDetails?.ccm_patient_archived !== true
    ) {
      firstRender.current = false;
      toasterId.current = enqueueSnackbar('Want to start the timer?', {
        variant: 'info',
        preventDuplicate: false,
        autoHideDuration: 10000,
        anchorOrigin: {vertical: 'bottom', horizontal: 'left'},
        content: key => (
          <StartTimerReminderToast
            id={key}
            ccmPatientData={{
              patientName: patientFullName
            }}
            onStartTime={handleStartTime}
          />
        )
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, hasCCMPermission]);

  useEffect(() => {
    return closeStartTimerReminder;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const renderErrorScreen = () => {
    return (
      <div className={classes.errorContainer}>
        <div className={classes.errorWrapper}>
          <p>{t('PATIENTS.UNABLE_TO_LOAD_PATIENT_DRAWER_DATA')}</p>
          <Button
            color="primary"
            variant="contained"
            className={'close-button'}
            onClick={closeDrawer}>
            {t('COMMON.CLOSE')}
          </Button>
        </div>
      </div>
    );
  };

  return (
    <Drawer
      disableEnforceFocus
      anchor={'right'}
      open={openHomeDrawer}
      className={[classes.drawer].join(' ')}
      onClose={closeDrawer}
      style={{
        zIndex: 1200
      }}>
      {isLoading && <div className={classes.loaderStyle}>{<Loader />}</div>}
      {isError && renderErrorScreen()}
      {!isLoading && !isError && (
        <div className="patient-details-home-wrapper">
          {/* TOB BAR */}
          <PatientDetailsTopBar closeDrawer={closeDrawer} />
          {/* Main Section */}
          <div className={'body-class'}>
            {/* Sidebar */}
            <div className={classes.leftDiv}>
              <PatientDetailsSideBar closeDrawer={closeDrawer} />
            </div>
            {/* Content */}
            <PatientDetailsContainer />
          </div>
        </div>
      )}
    </Drawer>
  );
};
export default PatientsDetailsHome;
